<template>
  <div
    class="default-container"
    :class="{ video: displayAs === 'video', attachment: displayAs === 'attachment', error: error }"
  >
    <input
      :v-bind="$attrs"
      :accept="accept"
      type="file"
      class="input-file"
      :disabled="isUploading"
      @change="onChange"
    />

    <div v-if="isUploading" class="loader"></div>

    <template v-else>
      <p v-if="!value">
        <feather-icon icon="PlusIcon" class="p-1 flex justify-center" />
        {{ text }}
      </p>

      <template v-else>
        <img v-if="displayAs === 'image'" :src="value || url" class="preview-image" />

        <video v-else-if="displayAs === 'video'" :src="value" class="preview-video responsive" controls></video>

        <div v-else class="preview-attachment max-w-full h-full flex justify-center flex-wrap">
          <a :href="value" target="_blank" class="flex justify-center flex-wrap max-w-full">
            <feather-icon icon="FileIcon" class="p-1 flex justify-center" />
            <p>{{ value }}</p>
          </a>

          <span @click="onClickRemoved" class="flex items-center justify-center text-sm">
            Remove <feather-icon icon="XIcon" class="w-4" />
          </span>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  props: {
    value: {
      type: [String, Object],
    },
    accept: {
      type: String,
    },
    displayAs: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      default: 'Upload',
    },
    presignedUrl: {
      type: String,
      default: '/attachments/upload',
    },
  },
  data() {
    return {
      file: null,
      fileType: '',
      previewUrl: '',
      isUploading: false,
      error: false,
    };
  },
  methods: {
    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },
    async onChange($event) {
      this.error = '';

      let file = null;

      if ($event.target.files && $event.target.files[0]) {
        file = $event.target.files[0];
      } else {
        return;
      }

      this.isUploading = true;
      this.$emit('on-uploading', true);
      try {
        const params = {
          fileName: Math.floor(Date.now() / 1000) + file.name,
          fileSize: this.formatBytes(file.size),
          mimeType: file.type,
        };

        // get presign
        const response = await axios.get(this.presignedUrl, { params });
        const { signedUrl, url } = response.data;

        // upload
        await axios
          .put(signedUrl, file, {
            headers: { 'Content-Type': file.type },
          })
          .then(() => {
            if (this.displayAs === 'attachment') {
              const payload = {
                fileSize: this.formatBytes(file.size),
                fileType: file.type,
                fileUrl: url,
              };
              this.$emit('input', payload);
              return this.$emit('on-success', payload);
            }

            this.$emit('input', url);
            return this.$emit('on-success', url);
          });
      } catch (error) {
        this.error = error;
        this.$emit('on-error', error);
      } finally {
        this.$emit('on-uploading', false);
        this.isUploading = false;
      }
    },
    onClickRemoved() {
      this.$emit('input', null);
    }
  },
  mounted() {},
};
</script>
<style lang="scss">
$base-width: 150px;
$base-height: 150px;
$base-video-width: 300px;
$base-video-height: 300px;

.default-container {
  border: 1px dashed #d9d9d9; /* the dash box */
  border-radius: 6px;
  background: #fafafa;
  transition: border-color 0.3s ease;
  padding: 10px;
  cursor: pointer;
  width: $base-width;
  height: $base-height;
  display: flex;
  justify-content: center;
  align-items: center;

  p {
    font-size: 12px;
    text-align: center;
  }

  &:hover {
    border: 1px dashed #1890ff;
  }

  .input-file {
    opacity: 0; /* invisible but it's there! */
    width: inherit;
    height: inherit;
    position: absolute;
    z-index: 1;
    cursor: pointer;
  }

  // if display didnt define
  // use default style
  .preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain; // or use cover;
  }
  .preview-attachment {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    p {
      font-size: 8px;
    }
  }
  .preview-video {
    width: 140px;
    height: 140px;
    position: absolute;
    z-index: 2;
  }
}

.video {
  width: $base-video-width;
  .preview-video {
    width: 250px;
    position: absolute;
    z-index: 2;
  }
}

.attachment {
  .preview-attachment {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    p {
      font-size: 8px;
    }
    z-index: 2;
  }
}

.error {
  border-color: red;
}

.loader {
  border: 5px solid #f3f3f3;
  border-radius: 50%;
  border-top: 5px solid #1890ff;
  width: 30px;
  height: 30px;
  -webkit-animation: spin 1s linear infinite; /* Safari */
  animation: spin 1s linear infinite;
}

/* Safari */
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
